import { Flex, Form, Segmented } from 'antd'
import { FormattedMessage } from 'react-intl'
import { ChangeHandlerOpts } from 'shared/hooks/useSyncNumericInputs'
import { DonationType } from '../../api/streamer/types'
import { formKeys } from '../DonationForm/constants'
import { FormValues } from '../../types'
import { ConvertAmountArgs } from '../../utils/convert'
import { lang } from './lang'
import TextDonation from './TextDonation'
import AudioDonation from './AudioDonation'
import Media from './Media'
import { useTypeOptions } from './hooks/useTypeOptions'
import { useMediaPricesLimits } from './hooks/useMediaPricesLimits'
import Label from '@/shared/components/Label'
import { useStreamerConfigContext } from '@/contexts/StreamerConfigContext/StreamerConfigContext'

type Props = {
  onSetFiatAmount: (value: string, opts?: ChangeHandlerOpts<Omit<ConvertAmountArgs, 'value'>>) => void
}

export default function DonationContent({ onSetFiatAmount }: Props) {
  const form = Form.useFormInstance<FormValues>()
  const { config, username = '' } = useStreamerConfigContext()
  const isMediaEnabled = config?.image_donation_config?.enabled || config?.video_donation_config?.enabled
  const fiatAmount = Form.useWatch<FormValues[typeof formKeys.fiatAmount]>(formKeys.fiatAmount)
  const baseCurrency = Form.useWatch<FormValues[typeof formKeys.baseCurrency]>(formKeys.baseCurrency)
  const cryptoCurrency = Form.useWatch<FormValues[typeof formKeys.selectedCryptoAsset]>(formKeys.selectedCryptoAsset)
  const donationType = Form.useWatch<FormValues[typeof formKeys.donationType]>(formKeys.donationType)

  const handleSetDonationType = (value: DonationType) => {
    form.setFieldValue(formKeys.donationType, value)
  }

  const handleSetFiatAmount = (value: string) =>
    onSetFiatAmount(value, { args: { currencyFrom: baseCurrency?.code, currencyTo: cryptoCurrency?.code, username } })

  const mediaPricesLimits = useMediaPricesLimits({ baseCurrencyCode: baseCurrency?.code, fiatAmount })
  const { availableDonationTypes } = useTypeOptions({ onSetDonationType: handleSetDonationType })

  const shouldShowButtons = availableDonationTypes.length > 1

  return (
    <Flex vertical gap={16}>
      {shouldShowButtons && (
        <Flex vertical gap={4}>
          <Label>
            <FormattedMessage {...lang.donationTypeTitle} />
          </Label>
          <Segmented options={availableDonationTypes} onChange={handleSetDonationType} size="large" block />
        </Flex>
      )}
      <Form.Item name={formKeys.donationType} noStyle />
      <Flex vertical gap={16}>
        {donationType === DonationType.Text && <TextDonation form={form} />}
        {donationType === DonationType.Audio && (
          <AudioDonation mediaPriceLimit={mediaPricesLimits.audio} onSetFiatAmount={handleSetFiatAmount} />
        )}
        {isMediaEnabled ? <Media mediaPricesLimits={mediaPricesLimits} onSetFiatAmount={handleSetFiatAmount} /> : null}
      </Flex>
    </Flex>
  )
}
